import React, { useEffect, useMemo, useRef, useState } from "react";
import { LEARN } from "../theme";
import { makeTargetLayout } from "./writingGeometryEvaluator";
import { formationStrokes } from "./writingReferenceTemplates";
import { hexAlpha, trimPoints } from "./WritingSketchCanvas";
import { paperSurface } from "./games/shared";

/**
 * Pip draws the letter. Kindergarten formation strokes (top-to-bottom,
 * left-to-right, numbered start dots) animate one after another with a
 * moving pencil that lifts and hops between strokes.
 *
 * Self-driving by default (`play`); pass an external `progress` (0…1) to
 * drive it yourself. `onFinished` fires once when the last stroke lands.
 */
const HOP_MS = 260;
const HOLD_MS = 320;

const WritingFormationDemo = ({
  letter,
  accent = LEARN.color.lavender,
  progress: externalProgress = null,
  play = true,
  speed = 1,
  reduceMotion = false,
  onFinished,
  showNumbers = true,
  showLines = true,
  ariaLabel,
  style,
}) => {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ width: 520, height: 300 });
  const [progress, setProgress] = useState(externalProgress ?? 0);
  const finishedRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const update = () => {
      const next = { width: Math.max(1, node.clientWidth), height: Math.max(120, node.clientHeight) };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Glyph frame from the same layout the pad uses, strokes from the formation set.
  const model = useMemo(() => {
    const layout = makeTargetLayout(letter, size);
    const frame = layout.glyphs[0]?.frame;
    if (!frame) return null;
    const strokes = formationStrokes(letter).map((stroke) => {
      const points = stroke.points.map((p) => ({ x: frame.x + p.x * frame.width, y: frame.y + p.y * frame.height }));
      const start = { x: frame.x + stroke.start.x * frame.width, y: frame.y + stroke.start.y * frame.height };
      let length = 0;
      for (let i = 1; i < points.length; i += 1) length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
      return { points, start, length, prompt: stroke.prompt };
    });
    const total = strokes.reduce((sum, s) => sum + s.length, 0) || 1;
    // Timeline: each stroke = hop + draw (draw time proportional to length, min 480ms).
    const segments = [];
    let cursor = 0;
    strokes.forEach((stroke, index) => {
      const hop = index === 0 ? HOP_MS * 0.6 : HOP_MS;
      const draw = Math.max(480, (stroke.length / total) * 1900);
      segments.push({ hopStart: cursor, drawStart: cursor + hop, end: cursor + hop + draw });
      cursor += hop + draw;
    });
    return { frame, strokes, segments, duration: (cursor + HOLD_MS) / Math.max(0.2, speed) };
  }, [letter, size, speed]);

  // Self-driving timeline.
  useEffect(() => {
    if (externalProgress != null) {
      setProgress(externalProgress);
      return undefined;
    }
    finishedRef.current = false;
    if (!play || !model) {
      setProgress(0);
      return undefined;
    }
    if (reduceMotion) {
      setProgress(1);
      const t = setTimeout(() => {
        if (!finishedRef.current) {
          finishedRef.current = true;
          onFinishedRef.current?.();
        }
      }, 120);
      return () => clearTimeout(t);
    }
    setProgress(0);
    let frame = 0;
    const started = performance.now();
    const tick = (now) => {
      const next = Math.min(1, (now - started) / model.duration);
      setProgress(next);
      if (next < 1) {
        frame = requestAnimationFrame(tick);
      } else if (!finishedRef.current) {
        finishedRef.current = true;
        onFinishedRef.current?.();
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [externalProgress, letter, model, play, reduceMotion]);

  useEffect(() => {
    if (externalProgress != null && externalProgress >= 1 && !finishedRef.current) {
      finishedRef.current = true;
      onFinishedRef.current?.();
    }
  }, [externalProgress]);

  const { width, height } = size;
  const strokeWidth = model ? Math.max(9, Math.min(22, model.frame.height * 0.075)) : 12;
  const time = model ? progress * model.duration * Math.max(0.2, speed) : 0;

  // Resolve what is drawn + where the pencil is.
  let pencil = null;
  let activeIndex = -1;
  const drawn = [];
  if (model) {
    model.strokes.forEach((stroke, index) => {
      const seg = model.segments[index];
      if (time >= seg.end) {
        drawn.push(stroke.points);
        return;
      }
      if (time >= seg.drawStart) {
        const local = (time - seg.drawStart) / Math.max(1, seg.end - seg.drawStart);
        const eased = local < 0.5 ? 2 * local * local : 1 - Math.pow(-2 * local + 2, 2) / 2;
        const partial = trimPoints(stroke.points, eased);
        drawn.push(partial);
        pencil = { ...partial[partial.length - 1], lifted: false };
        activeIndex = index;
      } else if (time >= seg.hopStart && pencil == null) {
        const from = index === 0 ? { x: stroke.start.x - model.frame.width * 0.2, y: stroke.start.y - model.frame.height * 0.25 } : lastPoint(model.strokes[index - 1].points);
        const t = (time - seg.hopStart) / Math.max(1, seg.drawStart - seg.hopStart);
        const arc = Math.sin(Math.PI * t) * model.frame.height * 0.12;
        pencil = { x: from.x + (stroke.start.x - from.x) * t, y: from.y + (stroke.start.y - from.y) * t - arc, lifted: true };
        activeIndex = index;
      }
    });
    if (!pencil && progress >= 1) {
      const last = lastPoint(model.strokes[model.strokes.length - 1]?.points || []);
      if (last) pencil = { x: last.x + 6, y: last.y - 4, lifted: true, done: true };
    }
  }

  const lines = model
    ? { top: model.frame.y + model.frame.height * 0.12, mid: model.frame.y + model.frame.height * 0.42, base: model.frame.y + model.frame.height * 0.88 }
    : null;

  return (
    <div
      ref={wrapRef}
      role="img"
      aria-label={ariaLabel || `Pip draws the letter ${letter}`}
      style={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        ...paperSurface(),
        overflow: "hidden",
        ...style,
      }}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ position: "absolute", inset: 0, display: "block" }} aria-hidden="true">
        {showLines && lines && (
          <g>
            <line x1={width * 0.05} x2={width * 0.95} y1={lines.top} y2={lines.top} stroke="rgba(11,26,51,0.11)" strokeWidth="1.5" />
            <line x1={width * 0.05} x2={width * 0.95} y1={lines.mid} y2={lines.mid} stroke={hexAlpha(accent, 0.35)} strokeWidth="2" strokeDasharray="7 8" />
            <line x1={width * 0.05} x2={width * 0.95} y1={lines.base} y2={lines.base} stroke={hexAlpha(accent, 0.5)} strokeWidth="2.5" />
          </g>
        )}
        {model && model.strokes.map((stroke, index) => (
          <polyline
            key={`ghost-${index}`}
            points={stroke.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke={hexAlpha(accent, 0.13)}
            strokeWidth={strokeWidth * 1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {drawn.map((points, index) => (
          <polyline
            key={`ink-${index}`}
            points={points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke={accent}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {showNumbers && model && model.strokes.map((stroke, index) => {
          const done = model.segments[index] && time >= model.segments[index].end;
          const active = index === activeIndex;
          const r = Math.max(11, Math.min(16, model.frame.height * 0.036));
          return (
            <g key={`dot-${index}`} opacity={done ? 0.35 : 1}>
              {active && !reduceMotion && (
                <circle cx={stroke.start.x} cy={stroke.start.y} r={r * 1.9} fill={hexAlpha(accent, 0.18)}>
                  <animate attributeName="r" values={`${r * 1.2};${r * 2.2};${r * 1.2}`} dur="1.1s" repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={stroke.start.x} cy={stroke.start.y} r={r} fill={accent} stroke="#fff" strokeWidth="2.5" />
              <text x={stroke.start.x} y={stroke.start.y + r * 0.45} textAnchor="middle" fontFamily={LEARN.font} fontWeight="700" fontSize={r * 1.3} fill="#fff">
                {index + 1}
              </text>
            </g>
          );
        })}
        {pencil && <Pencil x={pencil.x} y={pencil.y} lifted={pencil.lifted} size={Math.max(44, Math.min(96, (model?.frame.height || 200) * 0.32))} accent={accent} />}
      </svg>
    </div>
  );
};

function lastPoint(points) {
  return points?.length ? points[points.length - 1] : null;
}

/** Inline crayon-pencil, tip exactly at (x, y), leaning up-right. */
function Pencil({ x, y, lifted, size, accent }) {
  const w = size * 0.22;
  const h = size;
  const lift = lifted ? -size * 0.16 : 0;
  return (
    <g transform={`translate(${x} ${y + lift}) rotate(-38)`}>
      {/* shadow when lifted */}
      {lifted && <ellipse cx={0} cy={-lift + 4} rx={w * 0.9} ry={w * 0.35} fill="rgba(11,26,51,0.12)" />}
      {/* graphite tip */}
      <polygon points={`0,0 ${-w * 0.22},${-h * 0.09} ${w * 0.22},${-h * 0.09}`} fill={accent} />
      {/* wood */}
      <polygon points={`${-w * 0.22},${-h * 0.09} ${w * 0.22},${-h * 0.09} ${w * 0.5},${-h * 0.24} ${-w * 0.5},${-h * 0.24}`} fill="#F3D9A4" />
      {/* body */}
      <rect x={-w * 0.5} y={-h * 0.24 - h * 0.62} width={w} height={h * 0.62} fill={LEARN.color.sun} rx={w * 0.08} />
      <rect x={-w * 0.5} y={-h * 0.24 - h * 0.62} width={w * 0.3} height={h * 0.62} fill="rgba(255,255,255,0.35)" />
      {/* ferrule + eraser */}
      <rect x={-w * 0.5} y={-h * 0.24 - h * 0.62 - h * 0.05} width={w} height={h * 0.05} fill="#C9D3E0" />
      <rect x={-w * 0.5} y={-h * 0.24 - h * 0.62 - h * 0.14} width={w} height={h * 0.09} fill={LEARN.color.coral} rx={w * 0.2} />
    </g>
  );
}

export default WritingFormationDemo;
